import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/AboutPage.css";
import { getIdentifier } from "../utils/auth";
import PublicHeader from "../components/PublicHeader";
import "../styles/PublicHeader.css";

const API_BASE = "https://squid-app-6s2hc.ondigitalocean.app";
const GET_USER_URL = `${API_BASE}/get-user`;

const ROUTE_CREATE_COLLECTION = "/upload-collection";
const ROUTE_VIEW_COLLECTIONS = "/view-collection-selector";
const ROUTE_EDIT_COLLECTIONS = "/edit-collection-selector";


const AboutMe = () => {
  const navigate = useNavigate();
  const location = useLocation();
  

  const [identifier, setIdentifier] = useState(() => getIdentifier() || "");
  const isAuthed = !!identifier;

  const [menuOpen, setMenuOpen] = useState(false);
  const [hasCollections, setHasCollections] = useState(false);

  const idPayload = useMemo(
    () => (id) => (id && id.includes("@") ? { email: id } : { username: id }),
    []
  );

  useEffect(() => {
    const sync = () => setIdentifier(getIdentifier() || "");
    sync();

    window.addEventListener("focus", sync);
    window.addEventListener("storage", sync);

    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.key]);

  useEffect(() => {
    let alive = true;

    async function loadHasCollections() {
      if (!identifier) {
        if (alive) setHasCollections(false);
        return;
      }

      try {
        const res = await fetch(GET_USER_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(idPayload(identifier)),
        });

        const data = await res.json().catch(() => ({}));

        if (!alive) return;

        const has =
          !!data?.hasCollection ||
          !!data?.hasCollections ||
          (Array.isArray(data?.collections) &&
            data.collections.length > 0);

        setHasCollections(has);
      } catch {
        if (alive) setHasCollections(false);
      }
    }

    loadHasCollections();

    return () => {
      alive = false;
    };
  }, [identifier, idPayload]);

  // open data accordion if linked
useEffect(() => {
  const params = new URLSearchParams(location.search);
  const section = params.get("section");

  if (section === "data-usage") {

    // desktop / details version
    const target = document.getElementById("data-usage");

    if (target) {
      target.open = true;

      setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 80);
    }
  }
}, [location.search]);

  const go = (path) => () => {
    setMenuOpen(false);
    navigate(path);
  };

  const openTour = () => {
    setMenuOpen(false);
    navigate("/overview");
  };

  return (
    <>
    {/* Top-right auth */}
      {/* BURGER */}
      {/* OVERLAY */}

      <div
        className={`menu-overlay ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* MENU */}

      <aside className={`slide-menu ${menuOpen ? "open" : ""}`}>
        <button
          className="menu-close"
          onClick={() => setMenuOpen(false)}
          aria-label="close navigation"
        >
          ×
        </button>

        <nav className="menu-nav">
          {!isAuthed ? (
            <>
              <button onClick={go("/welcome")}>welcome</button>
               <div className="menu-divider" />

              <button onClick={openTour}>see how it works</button>

              <button onClick={go("/discover")}>
                browse collections
              </button>
            </>
          ) : (
            <>
              <button onClick={go("/home-page")}>
                home
              </button>

              <button onClick={go("/profile")}>
                profile
              </button>


              <div className="menu-divider" />

              <button onClick={go(ROUTE_CREATE_COLLECTION)}>
                create collection
              </button>

              {hasCollections && (
                <>
                  <button onClick={go(ROUTE_VIEW_COLLECTIONS)}>
                    view my collection(s)
                  </button>

                  <button onClick={go(ROUTE_EDIT_COLLECTIONS)}>
                    edit my collection(s)
                  </button>
                </>
              )}

              <div className="menu-divider" />

               <button onClick={go("/discover")}>
                browse collections
              </button>

              <button onClick={go("/contact")}>
                contact t.c.p.
              </button>
            </>
          )}
        </nav>
      </aside>
      
<PublicHeader
    mobileNavOpen={menuOpen}
    setMobileNavOpen={setMenuOpen}
    showAuth={!isAuthed}
/>

      {/* PAGE */}

      <main className="about-page">
        <section className="about-layout">

          {/* LEFT */}

          <div className="about-left">
      <h1 className="about-title">
        t.c.p.
        <br />
        helps you
        <br />
        organise,
        <br />
        document,
        <br />
        and share
        <br />
        your
        <br />
        collections.
      </h1>
         <p className="about-lead">
  hi, i’m sarah haq. i’m the founder and
  developer of the collectors platform (t.c.p.).
  i’m also an art collector with ten boxes of
  artworks currently in storage somewhere in hamburg.
  <br /><br />
  you can check out my
  personal collection{" "}
  <a
    href="https://thecollectorsplatform.com/p/v5Vf0B4?tags=unique"
    target="_blank"
    rel="noopener noreferrer"
    className="about-inline-link"
  >
    here
  </a>.
</p>
          </div>

          {/* RIGHT */}

         <div className="about-right">

  <details className="about-item">
    <summary>why the collectors platform?</summary>

    <div className="about-item-content">
     <p>
  i bought my first artwork from a
  gallery in london in...
</p>

<p>
  hold on, let me dig for the exact date.
</p>

<p>
  (searching through emails...)
</p>

<p>
  26 september 2015.
</p>

<p>
  that took longer than it should have.
</p>

<p>
  and then there are all the other details.
  where did i buy it from? 
  did they give me a certificate
  of authenticity? where is it?
  why did i buy it when i definitely
  couldn’t afford it at the time?
</p>

<p>
  since then, i’ve moved from london
  to berlin to hamburg, my collection
  has continued to grow, and it became
  increasingly difficult to manage
  the collection and keep track of
  details scattered across invoices,
  folders, certificates, storage boxes,
  emails, and memory.
</p>
<p>
  as i became more nomadic and parts
  of my collection ended up spread
  across different homes, storage units,
  and cities, i also wanted a practical
  way to keep track of where everything
  was located.
</p>
<p>
  and then there was the emotional side. you’re never just buying
  an artwork or a physical object. there’s the story of the artist,
  the piece,
  and your own memories attached to it.
</p>

<p>
  i couldn’t find an existing tool that
  worked the way i needed it to,
  so i built t.c.p.
</p>

    </div>
  </details>

  <details className="about-item">
    <summary>what is it? what makes it different?</summary>

    <div className="about-item-content">
     <p>
  t.c.p. gives you one
  place to store images, item details,
  invoices, certificates, locations,
  and memories — so nothing gets lost.
</p>

<p>
  most existing tools feel too rigid
  or too static. you upload one item
  at a time, can only store certain
  information, and usually have to choose
  between fully public or fully private.
</p>

<p>
  t.c.p. lets you add many items at once,
  reorganise collections as they grow,
  attach documents, and control what’s
  public or private — not just entire
  collections, but individual details too.
</p>
    </div>
  </details>

  <details className="about-item">
    <summary>who is it for?</summary>

    <div className="about-item-content">
  
<p>
  it’s for artists, galleries, and small business owners whose collections and inventory are always evolving.
  it’s for the nomad trying to keep track
  of a collection spread across homes,
  countries, or storage units.
</p>

<p>
  it’s for the private collector looking
  for something more flexible than social media.
  it’s for people with terrible memories and
  information scattered everywhere.
</p>

<p>
  it’s for anyone who is collecting more than
  objects and wants to preserve the items,
  stories, and details behind what they own
  in a quieter digital space — without feeds,
  noise, or pressure to perform.
</p>
    </div>
  </details>

  <details className="about-item">
    <summary>what does it cost?</summary>
<div className="about-item-content">
  <p>
    it’s completely free to sign up and explore the platform.
  </p>

  <p>
    creating and managing collections is currently free for
    early members, and that won’t change for collectors who
    join now.
  </p>
</div>
  </details>

  <details className="about-item">
    <summary>what can i upload?</summary>

    <div className="about-item-content">
   <p>
  collections can be anything from artworks
  to labubu dolls. all that’s required is
  at least one image upload per item.
</p>

<p>
  you can also add invoices, certificates,
  dimensions, materials,
  locations, and stories about each item.
</p>
    </div>
  </details>

<details className="about-item">
  <summary>the mission</summary>

<div className="about-item-content">

  <p>
    i wanted to build something cool and real — words i wrote in my journal when i first started development. 
  </p>

  <p>
    t.c.p. is a human-centred digital product where collectors can showcase their collections and share the stories behind all their wonderful pieces.
  </p>

  <p>
    i love and miss my own art collection. at the very least, i wanted to give it a digital home. i also want to build a company that creates opportunities for people without traditional experience, where mental health and work-life balance are not buzzwords, but part of what t.c.p. is built on.
  </p>

  <p>
    i want t.c.p. to remain easy to use and shaped by the people who use it. there is enough noise online already, and t.c.p. aims to cut through it.
  </p>

</div>

</details>

<details className="about-item" id="data-usage">
    <summary>how is my data used?</summary>

    <div className="about-item-content">
     <p>
  your data is stored only so the platform
  can function for you.
</p>

<p>
  there are no ads, trackers,
  or selling of user data.
</p>

<p>
  i don’t send marketing emails.
  the only emails you’ll receive are essential
  ones — for example, password resets or
  important platform updates.
</p>

<p>
  you decide what stays private and what
  becomes public, and you can delete your
  collection or account at any time.
</p>
    </div>
  </details>

  <details className="about-item">
  <summary>reach out · contact info</summary>

  <div className="about-item-content contact-content">

    <p>
  feedback is always welcome. please reach out, share ideas, and help shape the future of t.c.p.

</p>
    <div className="contact-row">
      <span className="contact-label">email</span>
      <a
        href="mailto:shaq@thecollectorsplatform.com"
        className="contact-link"
      >
        shaq@thecollectorsplatform.com
      </a>
    </div>

    <div className="contact-row">
      <span className="contact-label">instagram</span>
      <a
        href="https://instagram.com/thecollectorsplatform"
        target="_blank"
        rel="noopener noreferrer"
        className="contact-link"
      >
        @thecollectorsplatform
      </a>
    </div>

  </div>
</details>

</div>

        </section>
      </main>
    </>
  );
};

export default AboutMe;